// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Trade-partner scorecards — the "who should I invite to bid?" view.
// Aggregates a partner's history across every project: how often they bid,
// how often they win, and how their awarded contracts land against your
// budget. Pure functions over loaded project data; no React, no DB.
//
// Partners are stored per-project with project-local ids, but the same firm
// recurs across projects, so we key the aggregate by NORMALIZED NAME.

import type { Bid, BidPartner, BidScope } from "../projectData";
import type { BuyoutItem } from "../types";

const AWARDED_STATUSES = new Set(["awarded", "executed"]);
const DAY_MS = 86_400_000;

export interface ProjectBidData {
  id: string;
  name: string;
  bids: Bid[];
  bidPartners: BidPartner[];
  bidScopes?: BidScope[];
  buyoutItems: BuyoutItem[];
}

export interface PartnerScore {
  name: string;            // display name (first non-empty seen)
  trade: string;           // best-known trade label
  packageCount: number;    // distinct (project, scope) the partner bid on
  winCount: number;        // packages awarded to them (via bid→buyout link)
  winRate: number;         // winCount / packageCount  (0–1)
  avgVariancePct: number | null; // mean (contract−budget)/budget over wins w/ budget
  /** How many wins the average above is built from. One sample is an anecdote,
   *  and the table has to be able to say so. */
  varianceSamples: number;
  /** CSI divisions this firm has actually bid in, taken from the scopes they
   *  were invited to rather than the free-text trade label — which is typed by
   *  hand and so cannot be filtered on reliably. */
  divisionIds: string[];
  projectCount: number;    // distinct projects bid on
  lastBidISO: string;      // most recent bid date
  lastBidDaysAgo: number | null;
}

function norm(name: string): string {
  return (name || "").trim().toLowerCase();
}

interface Acc {
  name: string;
  trade: string;
  packages: Set<string>;
  projects: Set<string>;
  wins: Set<string>;
  varianceSamples: number[];
  divisions: Set<string>;
  lastBidMs: number;
  lastBidISO: string;
}

/** Aggregate partner performance across projects. nowMs injectable for tests. */
export function computePartnerScores(
  projects: ProjectBidData[],
  nowMs: number = Date.now(),
): PartnerScore[] {
  const acc = new Map<string, Acc>();

  const ensure = (key: string, name: string, trade: string): Acc => {
    let a = acc.get(key);
    if (!a) {
      a = { name, trade, packages: new Set(), projects: new Set(), wins: new Set(), varianceSamples: [], divisions: new Set(), lastBidMs: 0, lastBidISO: "" };
      acc.set(key, a);
    }
    // Backfill display name / trade if a better value shows up later.
    if (!a.name && name) a.name = name;
    if (!a.trade && trade) a.trade = trade;
    return a;
  };

  for (const proj of projects) {
    const partnerById = new Map(proj.bidPartners.map((p) => [p.id, p]));
    const bidById = new Map(proj.bids.map((b) => [b.id, b]));
    const scopeById = new Map((proj.bidScopes || []).map((s) => [s.id, s]));

    // Pass 1 — submitted bids → packages / projects / recency.
    for (const bid of proj.bids) {
      const partner = partnerById.get(bid.partnerId);
      const name = partner?.name || "";
      const key = norm(name);
      if (!key) continue; // can't attribute a nameless partner
      const a = ensure(key, name, partner?.trade || "");
      a.packages.add(`${proj.id}:${bid.scopeId}`);
      a.projects.add(proj.id);
      for (const d of scopeById.get(bid.scopeId)?.divisionIds || []) a.divisions.add(d);
      const t = Date.parse(bid.date || "");
      if (!isNaN(t) && t > a.lastBidMs) { a.lastBidMs = t; a.lastBidISO = bid.date; }
    }

    // Pass 2 — awarded buyouts linked to a bid → wins / variance.
    for (const bo of proj.buyoutItems || []) {
      if (!AWARDED_STATUSES.has(bo.status) || !bo.bidId) continue;
      const bid = bidById.get(bo.bidId);
      if (!bid) continue;
      const partner = partnerById.get(bid.partnerId);
      const key = norm(partner?.name || "");
      if (!key) continue;
      const a = ensure(key, partner?.name || "", partner?.trade || "");
      a.wins.add(`${proj.id}:${bid.scopeId}`);
      const budget = Number(bo.budgetAmount) || 0;
      const contract = Number(bo.contractAmount) || 0;
      if (budget > 0 && contract > 0) a.varianceSamples.push((contract - budget) / budget);
    }
  }

  const scores: PartnerScore[] = [];
  for (const a of acc.values()) {
    const packageCount = a.packages.size;
    const winCount = a.wins.size;
    scores.push({
      name: a.name,
      trade: a.trade,
      packageCount,
      winCount,
      winRate: packageCount > 0 ? winCount / packageCount : 0,
      avgVariancePct: a.varianceSamples.length
        ? a.varianceSamples.reduce((s, v) => s + v, 0) / a.varianceSamples.length
        : null,
      varianceSamples: a.varianceSamples.length,
      divisionIds: [...a.divisions].sort(),
      projectCount: a.projects.size,
      lastBidISO: a.lastBidISO,
      lastBidDaysAgo: a.lastBidMs > 0 ? Math.floor((nowMs - a.lastBidMs) / DAY_MS) : null,
    });
  }

  // Most-engaged partners first: by packages bid, then win rate.
  scores.sort((x, y) => y.packageCount - x.packageCount || y.winRate - x.winRate);
  return scores;
}
